/**
 * Make (Integromat) Integration Settings
 *
 * Make uses a webhook URL (from a Make scenario "Webhooks" trigger).
 * Velaro POSTs conversation data to that URL; Make can return variables.
 *
 * Setup: In Make, create a scenario with the "Custom Webhook" trigger.
 * Copy the webhook URL and paste it here.
 */
import React, { useEffect, useState } from "react";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import IntegrationActivityLog from "../IntegrationActivityLog";

interface Config {
  configured: boolean;
  displayName: string;
  isEnabled: boolean;
  webhookUrlMasked: string;
}

function MakeLogo() {
  return (
    <svg viewBox="0 0 100 100" className="w-10 h-10" fill="none">
      <rect width="100" height="100" rx="18" fill="#6D00CC" />
      <circle cx="50" cy="50" r="22" stroke="white" strokeWidth="7" fill="none" />
      <circle cx="50" cy="28" r="6" fill="white" />
      <circle cx="50" cy="72" r="6" fill="white" />
      <circle cx="28" cy="50" r="6" fill="white" />
      <circle cx="72" cy="50" r="6" fill="white" />
    </svg>
  );
}

export default function MakePage() {
  const api = useApi();
  const [config, setConfig] = useState<Config | null>(null);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.messaging.get("Make/config")
      .then(r => r.ok ? r.json() : null)
      .then((data: Config | null) => {
        if (data) {
          setConfig(data);
          setDisplayName(data.displayName || "");
        } else {
          setConfig({ configured: false, displayName: "", isEnabled: false, webhookUrlMasked: "" });
        }
      })
      .catch(() => setConfig({ configured: false, displayName: "", isEnabled: false, webhookUrlMasked: "" }));
  }, []);

  async function save() {
    if (!webhookUrl.trim()) { setError("Webhook URL is required"); return; }
    setSaving(true);
    setError("");
    try {
      const res = await api.messaging.post("Make/config", {
        webhookUrl,
        displayName,
        isEnabled: true
      });
      if (res.ok) {
        setSaved(true);
        const updated = await api.messaging.get("Make/config").then(r => r.json());
        setConfig(updated);
        setWebhookUrl("");
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError("Failed to save. Check the webhook URL and try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 border border-slate-300 rounded p-2 flex items-center justify-center">
          <MakeLogo />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-slate-800 text-lg">Make (Integromat)</div>
          <div className="text-slate-400 text-sm">
            Trigger Make scenarios from conversations and workflow nodes. Connect to 1,000+ apps.
          </div>
        </div>
        {config?.configured && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1">
            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Connected
          </span>
        )}
      </div>

      {/* Status bar */}
      {config?.configured && (
        <div className="mb-6 rounded-lg border border-slate-200 p-4 bg-slate-50 text-sm">
          <div className="font-medium text-slate-700 mb-1">Active Scenario</div>
          <div className="text-slate-500">{config.displayName || "Unnamed scenario"}</div>
          <div className="text-slate-400 font-mono text-xs mt-1">{config.webhookUrlMasked}</div>
        </div>
      )}

      {/* Feature cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {[
          { icon: "⚡", title: "Workflow Node", desc: "Add a Make step in any workflow. Velaro sends full conversation context and the scenario can return variables." },
          { icon: "🔗", title: "1,000+ Apps", desc: "Connect to CRMs, databases, Slack, Google Sheets, Airtable, and more — no code required." },
          { icon: "🔄", title: "Bidirectional", desc: "Make can return key-value pairs that Velaro saves as conversation variables for use in later workflow steps." }
        ].map(f => (
          <div key={f.title} className="rounded-lg border border-slate-200 p-4">
            <div className="text-2xl mb-2">{f.icon}</div>
            <div className="font-medium text-slate-800 text-sm mb-1">{f.title}</div>
            <div className="text-xs text-slate-500">{f.desc}</div>
          </div>
        ))}
      </div>

      {/* Setup wizard */}
      <div className="mb-6 rounded-lg border border-violet-200 bg-violet-50 p-5">
        <div className="font-semibold text-violet-800 mb-3">Quick Setup (3 steps)</div>
        <ol className="space-y-2 text-sm text-violet-700">
          <li className="flex items-start gap-2">
            <span className="shrink-0 w-5 h-5 rounded-full bg-violet-200 text-violet-800 text-xs font-bold flex items-center justify-center">1</span>
            <span>In Make, create a new scenario and add the <strong>Custom Webhook</strong> trigger as the first module.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="shrink-0 w-5 h-5 rounded-full bg-violet-200 text-violet-800 text-xs font-bold flex items-center justify-center">2</span>
            <span>Copy the <strong>Webhook URL</strong> Make provides and paste it in the field below.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="shrink-0 w-5 h-5 rounded-full bg-violet-200 text-violet-800 text-xs font-bold flex items-center justify-center">3</span>
            <span>Add a <strong>Make</strong> node to any workflow in Velaro — it will POST conversation data to your scenario.</span>
          </li>
        </ol>
      </div>

      {/* Configuration form */}
      <div className="border border-slate-200 rounded-lg p-5">
        <div className="font-medium text-slate-800 mb-4">
          {config?.configured ? "Update Webhook URL" : "Connect Make"}
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Scenario Name (optional)</label>
            <input
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
              placeholder="e.g. Lead Capture Scenario"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Make Webhook URL *</label>
            <input
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-400"
              placeholder="https://hook.eu1.make.com/..."
              value={webhookUrl}
              onChange={e => { setWebhookUrl(e.target.value); setError(""); }}
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          {saved && <p className="text-xs text-green-600">Saved successfully.</p>}
        </div>
        <div className="mt-4">
          <PrimaryBlueButton label={saving ? "Saving..." : "Save"} onClick={save} prefixIcon={null} />
        </div>
      </div>

      {/* Payload reference */}
      <div className="mt-6 border border-slate-200 rounded-lg p-5">
        <div className="font-medium text-slate-800 mb-3">What Velaro sends to your scenario</div>
        <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-3 overflow-auto text-slate-700">{`{
  "jobId": 12345,
  "siteId": 1001,
  "conversationId": 98765,
  "contactEmail": "visitor@example.com",
  "contactName": "John Smith",
  "agentName": "Sarah Lee",
  "sentiment": "positive",
  "workflowVariables": {
    "orderNumber": "ORD-1234",
    "issueType": "Billing"
  }
}`}</pre>
        <p className="text-xs text-slate-500 mt-2">
          Your scenario can respond with a JSON object — Velaro saves the keys as workflow variables
          available as <code className="bg-slate-100 px-1 rounded">{"<<workflow.keyName>>"}</code>.
        </p>
      </div>

      {/* Activity Log */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Activity Log</h2>
        <IntegrationActivityLog integrationSlug="Make" />
      </div>
    </div>
  );
}
